import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { TooltipItem } from 'chart.js';
import { ReactNode, useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import { FeatureEnum } from '../../services/data/impl/cube/cubeSlice';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { useBlueberryFeatureIcon } from '../commonUI/BlueberryIcons';
import { selectCurrBlueberryClass } from '../select/selectSlice';

export type TooltipData = {
  dataPoints: TooltipItem<'bar'>[];
  left: number;
  bottom: number;
};

interface RowsideChartTooltipProps {
  data: TooltipData;
  chartArea?: {
    bottom: number;
    height: number;
    left: number;
    right: number;
    top: number;
    width: number;
  };
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export const RowsideChartTooltip = ({
  data,
  chartArea,
  open,
  onOpen,
  onClose,
}: RowsideChartTooltipProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);

  const blueberryTextColorMap: { [key: string]: string } = {
    [FeatureEnum.blueberry_Green]: theme.colors.blueberryColors.green,
    [FeatureEnum.blueberry_Yellow]: theme.colors.blueberryColors.pink,
    [FeatureEnum.blueberry_Purple]: theme.colors.blueberryColors.purple,
    [FeatureEnum.blueberry_Blue]: theme.colors.blueberryColors.blue,
    [FeatureEnum.flower]: theme.colors.blueberryColors.flower,
    [FeatureEnum.flower_closed]: theme.colors.blueberryColors.closed,
    [FeatureEnum.flower_open]: theme.colors.blueberryColors.open,
    [FeatureEnum.flower_petalfall]: theme.colors.blueberryColors.petalfall,
  };

  const tooltipItem = (
    label: string,
    color: string,
    value: string | number,
    icon?: ReactNode
  ) => {
    return (
      <Box
        key={label}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '2rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '12px',
                height: '12px',
                aspectRatio: '1/1',
                mr: '0.5rem',
                mb: '1.5px',
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            color={color}
            sx={{
              mr: '0.5rem',
            }}
          >
            {/* Make flowers plural */}
            {(label as FeatureEnum) === FeatureEnum.flower
              ? t('Flowers')
              : t(label as FeatureEnum)}
          </Typography>
        </Box>
        <Typography align='right'>{value}</Typography>
      </Box>
    );
  };

  const getBlueberryIcon = useBlueberryFeatureIcon();

  const tooltipContent = useMemo(() => {
    if (!data.dataPoints) {
      return null;
    }
    let total = 0;

    const content = data.dataPoints
      ?.filter((item) => {
        return item.raw !== null;
      })
      .map((item) => {
        const value = item.dataset.data[item.dataIndex] as number;
        total = total + value;
        return tooltipItem(
          item.dataset.label || '',
          blueberryTextColorMap[item.dataset.label as FeatureEnum],
          item.formattedValue,
          getBlueberryIcon(item.dataset.label as FeatureEnum, 0.75)
        );
      });

    return (
      content.length > 0 && (
        <Box margin={'0.5rem'}>
          {content}
          {content.length > 1 &&
            tooltipItem('Total', theme.colors.common.white, total)}
        </Box>
      )
    );
  }, [data.left, data.bottom, currBlueberryClass]);

  if (!chartArea) return null;

  return (
    <Tooltip
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      arrow
      placement='top'
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -data.bottom],
              },
            },
          ],
        },
      }}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: theme.colors.darkTheme.grey.dark,
            display: 'flex',
          },
        },
        arrow: {
          sx: {
            color: theme.colors.darkTheme.grey.dark,
          },
        },
      }}
      title={tooltipContent}
    >
      <Box
        sx={{
          position: 'absolute',
          left: data.left,
          top: chartArea.top,
        }}
      />
    </Tooltip>
  );
};
